import React, { useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from '@components/framework/input/InputNumber';
import { InputSwitch } from 'primereact/inputswitch';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { RuleHeader, TypedRuleAst } from '../../ruleAst';
import { useRuleMetadata } from '../../metadata/RuleMetadataProvider';
import ConditionTree from './ConditionTree';
import ActionList from './ActionList';

interface ComponentProps {
  ast: TypedRuleAst;
  header: RuleHeader;
  onAstChange: (updater: (previous: TypedRuleAst) => TypedRuleAst) => void;
  onHeaderChange: (header: Partial<RuleHeader>) => void;
  companyId?: string;
  readOnly?: boolean;
}

const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <section className="flex flex-col gap-2">
    <div>
      <h3 className="m-0 text-base font-medium">{title}</h3>
      {hint && <p className="m-0 text-sm text-content-secondary">{hint}</p>}
    </div>
    {children}
  </section>
);

/** The visual half of the editor: header, trigger, condition tree, actions. */
const RuleBuilder: React.FC<ComponentProps> = ({ ast, header, onAstChange, onHeaderChange, companyId, readOnly }) => {
  const { tr } = useTranslationLgs();
  const { metadata, triggerByKey, factsForTrigger, actionsForTrigger } = useRuleMetadata();

  const trigger = triggerByKey(ast.trigger?.type);
  const facts = useMemo(() => factsForTrigger(ast.trigger?.type ?? ''), [factsForTrigger, ast.trigger?.type]);
  const actions = useMemo(() => actionsForTrigger(ast.trigger?.type ?? ''), [actionsForTrigger, ast.trigger?.type]);

  return (
    <div className="flex flex-col gap-6">
      <Section title={tr('AiDispatcher.ruleHeader', 'Rule')}>
        <div className="flex flex-wrap items-start gap-4">
          <label className="flex min-w-[18rem] flex-1 flex-col gap-1">
            <span className="text-sm text-content-secondary">{tr('AiDispatcher.ruleName', 'Name')}</span>
            <InputText className="h-10 w-full" value={header.name} onChange={event => onHeaderChange({ name: event.target.value })} disabled={readOnly} />
          </label>
          <label className="flex w-32 shrink-0 flex-col gap-1">
            <span className="text-sm text-content-secondary">{tr('AiDispatcher.priority', 'Priority')}</span>
            <InputNumber
              className="h-10 w-full"
              inputClassName="h-10 w-full"
              value={header.priority}
              onValueChange={event => onHeaderChange({ priority: event.value ?? 0 })}
              disabled={readOnly}
            />
          </label>
          <div className="flex w-24 shrink-0 flex-col gap-1">
            <span className="text-sm text-content-secondary">{tr('AiDispatcher.enabled', 'Enabled')}</span>
            {/* The switch has no text line box of its own, so it needs the same explicit row
                height as the inputs beside it — otherwise it rides up and overlaps them. */}
            <div className="flex h-10 items-center">
              <InputSwitch checked={header.enabled} onChange={event => onHeaderChange({ enabled: event.value })} disabled={readOnly} />
            </div>
          </div>
        </div>
      </Section>

      <Section title={tr('AiDispatcher.trigger', 'When this happens')}>
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[20rem] flex-1">
            <Dropdown
              className="w-full"
              value={ast.trigger?.type || null}
              options={(metadata?.triggers ?? []).map(item => ({ label: tr(item.i18nKey, item.key), value: item.key }))}
              onChange={event =>
                onAstChange(previous => ({
                  ...previous,
                  trigger: { type: event.value },
                  // Facts are trigger-specific; a condition written for another trigger may name
                  // facts the new one cannot provide, so the author starts the condition again.
                  condition: undefined,
                }))
              }
              disabled={readOnly}
              placeholder={tr('AiDispatcher.selectTrigger', 'Select a trigger')}
            />
          </div>
          {trigger?.supportsDelay && (
            <label className="flex w-48 flex-col gap-1">
              <span className="text-sm text-content-secondary">{tr('AiDispatcher.delayMinutes', 'Wait (minutes)')}</span>
              <InputNumber
                value={ast.trigger?.delayMinutes ?? null}
                onValueChange={event => onAstChange(previous => ({ ...previous, trigger: { ...previous.trigger, delayMinutes: event.value ?? undefined } }))}
                disabled={readOnly}
              />
            </label>
          )}
        </div>
      </Section>

      <Section title={tr('AiDispatcher.conditions', 'And all this is true')} hint={tr('AiDispatcher.conditionsHint', 'Leave empty to run every time the trigger occurs.')}>
        <ConditionTree
          condition={ast.condition}
          facts={facts}
          onChange={condition => onAstChange(previous => ({ ...previous, condition }))}
          companyId={companyId}
          readOnly={readOnly}
        />
      </Section>

      <Section title={tr('AiDispatcher.actions', 'Then do this')}>
        <ActionList
          actions={ast.actions ?? []}
          available={actions}
          onChange={next => onAstChange(previous => ({ ...previous, actions: next }))}
          companyId={companyId}
          readOnly={readOnly}
        />
      </Section>
    </div>
  );
};

export default RuleBuilder;
